/* eslint-disable @typescript-eslint/no-explicit-any */
import React, { createContext, useCallback, useState, useContext } from 'react';
import Swal from 'sweetalert2';

import axios from 'axios';
import { useHistory } from 'react-router-dom';
import api from '../services/api';
import avatar from '~/assets/defaults/avatar.svg';
import { trackLogin } from '~/utils/ga4';

export interface IUser {
  id: number;
  name: string;
  email: string;
  additionalInformation: {
    id: number;
    document?: string;
    phone?: string;
    birthdate?: string;
    gender?: string;
    verified: boolean;
    created_at: string;
    address?: {
      id?: number;
      street: string;
      number?: string;
      neighborhood: string;
      city: string;
      state: string;
      country: string;
      complement?: string;
      zip_code: string;
    };
    avatar?: {
      archive_url: string;
    };
  };
}

interface AuthState {
  token: string;
  user: IUser;
}

interface SignInCredentials {
  email: string;
  password: string;
}

interface IGoogleData {
  access_token: string;
  token_type?: string;
  expires_in?: number;
  scope?: string;
  authuser?: string;
  prompt?: string;
}

interface AuthContextData {
  user: IUser;
  signIn(credentials: SignInCredentials): Promise<void>;
  signInGoogle(googleData: IGoogleData, inPurchase?: boolean): Promise<void>;
  signOut(): void;
  updateUser(user: IUser): void;
  activateAccount(data: { token: string; password: string }): Promise<void>;
  setAutoLoginData: React.Dispatch<React.SetStateAction<AuthState>>;
}

export const AuthContext = createContext<AuthContextData>(
  {} as AuthContextData
);

export const AuthProvider: React.FC = ({ children }) => {
  const history = useHistory();
  const [data, setData] = useState<AuthState>(() => {
    const token = localStorage.getItem('@Koro:token');
    const user = localStorage.getItem('@Koro:user');

    if (token && user) {
      api.defaults.headers.authorization = `Bearer ${token}`;
      return { token, user: JSON.parse(user) };
    }

    return {} as AuthState;
  });

  const signIn = useCallback(
    async ({ email, password }: SignInCredentials) => {
      try {
        const response = await api.post<AuthState>(
          `users/sessions`,
          {
            email,
            password,
          },
          {
            params: {
              environment: 'Atleta',
            },
          }
        );

        const { token, user } = response.data;

        if (!user.additionalInformation.verified) {
          localStorage.setItem(
            '@Koro:credentials',
            JSON.stringify({
              id: user.id,
              name: user.name,
              email,
              password,
            })
          );
          await api.post('users/sessions/resend-code', {
            email,
          });
          history.push(`${process.env.PUBLIC_URL}/cadastro/verificar-conta`);

          return;
        }

        localStorage.setItem('@Koro:token', token);
        localStorage.setItem(
          '@Koro:user',
          JSON.stringify({
            ...user,
            additionalInformation: {
              ...user.additionalInformation,
              avatar: user.additionalInformation.avatar || {
                archive_url: avatar,
              },
            },
          })
        );

        api.defaults.headers.authorization = `Bearer ${token}`;

        // GA4: track login
        trackLogin('email');

        setData({
          token,
          user,
        });
      } catch (error: any) {
        const { status } = error.response;

        if (status === 401) {
          Swal.fire(
            'Dados incorretos!',
            'Verifique o email e a senha e tente novamente.',
            'error'
          );
          return;
        }
        Swal.fire('Opss...', 'Ocorreu um erro tente novamente', 'error');
      }
    },
    [history]
  );

  const signInGoogle = useCallback(
    async (formData: IGoogleData, inPurchase?: boolean) => {
      try {
        const response = await api.post<AuthState>(
          `users/sessions/google`,
          formData,
          {
            params: {
              environment: 'Atleta',
            },
          }
        );

        const { token, user } = response.data;

        localStorage.setItem('@Koro:token', token);
        localStorage.setItem(
          '@Koro:user',
          JSON.stringify({
            ...user,
            additionalInformation: {
              ...user.additionalInformation,
              avatar: user.additionalInformation.avatar || {
                archive_url: avatar,
              },
            },
          })
        );

        api.defaults.headers.authorization = `Bearer ${token}`;

        // GA4: track login via Google
        trackLogin('google');

        setData({
          token,
          user,
        });
      } catch (error) {
        const err = error as any;
        if (err.response.data.message === 'User not found') {
          const response = await axios.get(
            `https://www.googleapis.com/oauth2/v3/userinfo`,
            {
              headers: {
                Authorization: `Bearer ${formData.access_token}`,
              },
            }
          );

          if (!inPurchase) {
            history.push(`${process.env.PUBLIC_URL}/cadastro`, {
              userData: response.data,
            });
          } else {
            const errorData = new Error('User not found') as any;

            errorData.data = response.data;

            throw errorData;
          }
        } else {
          Swal.fire('Opss...', 'Ocorreu um erro tente novamente', 'error');
        }
      }
    },
    [history]
  );

  const signOut = useCallback(async () => {
    localStorage.removeItem('@Koro:token');
    localStorage.removeItem('@Koro:user');

    if (window.ReactNativeWebView) {
      window.ReactNativeWebView.postMessage(
        JSON.stringify({
          singOut: true,
        })
      );
    }

    setData({} as AuthState);
  }, []);

  const updateUser = useCallback(
    (user: IUser) => {
      localStorage.setItem('@Koro:user', JSON.stringify(user));

      setData({
        token: data.token,
        user,
      });
    },
    [setData, data.token]
  );

  const activateAccount = useCallback(
    async ({
      token: activationToken,
      password,
    }: {
      token: string;
      password: string;
    }) => {
      const response = await api.post<AuthState>(
        `users/sessions/activate-account`,
        {
          token: activationToken,
          password,
        }
      );

      const { token, user } = response.data;

      localStorage.setItem('@Koro:token', token);
      localStorage.setItem(
        '@Koro:user',
        JSON.stringify({
          ...user,
          additionalInformation: {
            ...user.additionalInformation,
            avatar: user.additionalInformation.avatar || {
              archive_url: avatar,
            },
          },
        })
      );

      api.defaults.headers.authorization = `Bearer ${token}`;

      setData({
        token,
        user,
      });
    },
    []
  );

  return (
    <AuthContext.Provider
      value={{
        user: data.user,
        signIn,
        signOut,
        updateUser,
        signInGoogle,
        activateAccount,
        setAutoLoginData: setData,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
};

export function useAuth(): AuthContextData {
  const context = useContext(AuthContext);

  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }

  return context;
}
